import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';

import AddCreditModal from './AddCreditModal';
import SlabPricingModal from './SlabPricingModal';
import CreditLedgerModal from './CreditLedgerModal';
import CallexLoader from '../../../components/CallexLoader';
import AdminHeader from '../../../components/header/AdminHeader';

import { vbAccountsGet, useVbAccounts } from '../../../store/slices/voiceBroadcastBillingSlice';

// Rs with 2 dp and thousands separators. Values arrive as numeric strings.
export const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const VoiceBroadcastBilling = () => {
  const dispatch = useDispatch();
  const accounts = useVbAccounts();

  const [isLoading, setIsLoading] = useState(false);
  const [creditCompany, setCreditCompany] = useState(null); // company for top-up
  const [pricingCompany, setPricingCompany] = useState(null);
  const [ledgerCompany, setLedgerCompany] = useState(null);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      await dispatch(vbAccountsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const rows = accounts?.data || [];

  return (
    <>
      <AdminHeader
        title="VoiceBroadcast Billing"
        isAdd
        addBtnName="Add Credit"
        onAddClick={() => setCreditCompany({})}
        isRefresh
        onRefreshClick={() => fetchAccounts()}
      />

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Domain</th>
                  <th className="text-end">Total Credit</th>
                  <th className="text-end">Used Credit</th>
                  <th className="text-end">Pending Credit</th>
                  <th className="text-center">Charged Calls</th>
                  <th className="text-center">Unpaid Calls</th>
                  <th className="text-center">Status</th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((val, index) => {
                  const pending = Number(val?.available_credit || 0);
                  return (
                    <tr key={index}>
                      <td>{val?.company_name || `#${val?.company_id}`}</td>
                      <td>{val?.company_domain || '-'}</td>
                      <td className="text-end">{fmtMoney(val?.total_credit)}</td>
                      <td className="text-end">{fmtMoney(val?.used_credit)}</td>
                      <td
                        className={`text-end fw-bold ${
                          pending <= 0 ? 'text-danger' : val?.low_balance ? 'text-warning' : ''
                        }`}>
                        {fmtMoney(val?.available_credit)}
                      </td>
                      <td className="text-center">{val?.charged_calls ?? 0}</td>
                      <td
                        className={`text-center ${
                          Number(val?.unpaid_calls) > 0 ? 'text-danger fw-bold' : ''
                        }`}>
                        {val?.unpaid_calls ?? 0}
                      </td>
                      <td className="text-center">
                        {pending <= 0 ? (
                          <span className="text-danger">Dialing paused</span>
                        ) : val?.low_balance ? (
                          <span className="text-warning">Low balance</span>
                        ) : (
                          <span className="text-success">Active</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className="cursor-pointer text-success"
                            title="Add credit"
                            onClick={() => setCreditCompany(val)}>
                            <Icon.PlusCircle size={16} />
                          </div>
                          <div
                            className="cursor-pointer text-info"
                            title="Slab pricing"
                            onClick={() => setPricingCompany(val)}>
                            <Icon.Sliders size={16} />
                          </div>
                          <div
                            className="cursor-pointer text-primary"
                            title="Credit report"
                            onClick={() => setLedgerCompany(val)}>
                            <Icon.FileText size={16} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex flex-column justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <div>No company has prepaid credit yet.</div>
          <div className="mt-2 text-muted" style={{ fontSize: '13px' }}>
            Companies without a credit account are not credit-limited and keep dialing. Adding
            credit to a company starts metering it.
          </div>
        </Card>
      )}

      <AddCreditModal
        isOpen={!!creditCompany}
        company={creditCompany}
        onCloseClick={(apiCall = false) => {
          setCreditCompany(null);
          if (apiCall) fetchAccounts();
        }}
      />
      <SlabPricingModal
        isOpen={!!pricingCompany}
        company={pricingCompany}
        onCloseClick={() => setPricingCompany(null)}
      />
      <CreditLedgerModal
        isOpen={!!ledgerCompany}
        company={ledgerCompany}
        onCloseClick={() => setLedgerCompany(null)}
      />
    </>
  );
};

export default VoiceBroadcastBilling;
